import { useEffect, useState, useCallback } from "react";
import { Globe, Trash, FloppyDisk, MapPin, ArrowClockwise, X, MapTrifold } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { updateGeo, resetGeo } from "../lib/api";
import GeoMapPicker from "./GeoMapPicker";

/** Common ISO-3166-1 alpha-2 codes for the picker.
 *  Users can type any 2-letter code — this list is just for quick-add buttons.
 */
const POPULAR_COUNTRIES = [
  "US", "CA", "GB", "AU", "DE", "FR", "ES", "IT", "NL", "SE",
  "NO", "BR", "MX", "JP", "IN", "SG", "AE",
];

const US_STATES = [
  "US-CA", "US-NY", "US-TX", "US-FL", "US-WA", "US-IL", "US-PA", "US-MA",
];

const Chip = ({ label, onRemove, testid }) => (
  <span
    className="inline-flex items-center gap-1 bg-[#121212] border border-white/10 text-xs font-mono px-2 py-1 text-zinc-300"
    data-testid={testid}
  >
    {label}
    <button
      onClick={onRemove}
      className="text-zinc-500 hover:text-red-400 transition-colors"
      aria-label={`remove ${label}`}
    >
      <X size={10} />
    </button>
  </span>
);

export default function GeoTargetingDialog({ open, onOpenChange, adSetId, initialGeo, onSaved }) {
  const [mode, setMode] = useState("allow");
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [radii, setRadii] = useState([]);
  const [polygons, setPolygons] = useState([]);
  const [showMap, setShowMap] = useState(false);
  const [countryInput, setCountryInput] = useState("");
  const [stateInput, setStateInput] = useState("");
  const [cityInput, setCityInput] = useState("");
  const [radiusForm, setRadiusForm] = useState({ lat: "", lon: "", km: 50, label: "" });
  const [saving, setSaving] = useState(false);

  const hydrate = useCallback((g) => {
    setMode(g?.mode || "allow");
    setCountries(g?.countries || []);
    setStates(g?.states || []);
    setCities(g?.cities || []);
    setRadii(g?.radii || []);
    setPolygons(g?.polygons || []);
  }, []);

  useEffect(() => {
    if (open) hydrate(initialGeo);
  }, [open, initialGeo, hydrate]);

  const addCountry = () => {
    const c = countryInput.trim().toUpperCase();
    if (c.length === 2 && !countries.includes(c)) setCountries([...countries, c]);
    setCountryInput("");
  };

  const addState = () => {
    const s = stateInput.trim().toUpperCase();
    if (s && !states.includes(s)) setStates([...states, s]);
    setStateInput("");
  };

  const addCity = () => {
    const c = cityInput.trim();
    if (c && !cities.includes(c)) setCities([...cities, c]);
    setCityInput("");
  };

  const addRadius = () => {
    const lat = parseFloat(radiusForm.lat);
    const lon = parseFloat(radiusForm.lon);
    const km = parseFloat(radiusForm.km || 50);
    if (isNaN(lat) || isNaN(lon)) {
      toast.error("lat + lon must be numbers");
      return;
    }
    setRadii([...radii, { lat, lon, km, label: radiusForm.label || `${lat.toFixed(2)}, ${lon.toFixed(2)}` }]);
    setRadiusForm({ lat: "", lon: "", km: 50, label: "" });
  };

  const save = async () => {
    if (!adSetId) return;
    setSaving(true);
    try {
      await updateGeo(adSetId, { mode, countries, states, cities, radii });
      toast.success(
        `Geo saved · ${mode.toUpperCase()} · ${countries.length}c ${states.length}s ${cities.length}city ${radii.length}r`,
      );
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const clearAll = async () => {
    if (!adSetId) return;
    setSaving(true);
    try {
      await resetGeo(adSetId);
      hydrate(null);
      toast.success("Geo restrictions cleared");
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`bg-[#0a0a0a] border border-white/10 rounded-none ${showMap ? "max-w-4xl" : "max-w-2xl"} max-h-[90vh] overflow-y-auto`}
        data-testid="geo-targeting-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Globe size={22} weight="fill" className="text-[#ccff00]" />
            Geo targeting
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-sm">
            Server-side gate on the tracker redirect. Blocked visitors get
            <code className="text-[#ccff00] mx-1">410 GONE</code> or your
            fallback URL. Adds geo UTMs to allowed clicks.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-3">
          {/* Quick presets + map toggle */}
          <div className="flex items-center gap-2 flex-wrap border border-white/10 bg-[#050505] p-2">
            <span className="font-mono text-[10px] uppercase text-zinc-500 mr-1">quick:</span>
            <button
              data-testid="geo-preset-australia"
              onClick={() => {
                setMode("allow");
                setCountries(["AU"]);
                toast.success("Set to Australia-wide");
              }}
              className="px-2 py-1 bg-[#121212] border border-white/10 hover:border-[#ccff00] hover:text-[#ccff00] text-zinc-300 font-mono text-[10px] uppercase"
            >
              🇦🇺 Australia-wide
            </button>
            <button
              data-testid="geo-preset-au-major"
              onClick={() => {
                setMode("allow");
                setCountries(["AU"]);
                setStates(["AU-NSW", "AU-VIC", "AU-QLD", "AU-WA", "AU-SA"]);
                toast.success("Set to AU major states");
              }}
              className="px-2 py-1 bg-[#121212] border border-white/10 hover:border-[#ccff00] hover:text-[#ccff00] text-zinc-300 font-mono text-[10px] uppercase"
            >
              AU · 5 major states
            </button>
            <button
              data-testid="geo-preset-anz"
              onClick={() => {
                setMode("allow");
                setCountries(["AU", "NZ"]);
                toast.success("Set to Australia + New Zealand");
              }}
              className="px-2 py-1 bg-[#121212] border border-white/10 hover:border-[#ccff00] hover:text-[#ccff00] text-zinc-300 font-mono text-[10px] uppercase"
            >
              🇦🇺 + 🇳🇿 ANZ
            </button>
            <button
              data-testid="geo-toggle-map"
              onClick={() => setShowMap((s) => !s)}
              className={`ml-auto flex items-center gap-1 px-2 py-1 font-mono text-[10px] uppercase transition-colors border ${
                showMap
                  ? "bg-[#ccff00] text-black border-[#ccff00] font-bold"
                  : "bg-[#121212] border-white/10 hover:border-[#ccff00] hover:text-[#ccff00] text-zinc-300"
              }`}
            >
              <MapTrifold size={12} weight={showMap ? "fill" : "regular"} />
              {showMap ? "hide map" : "open map"}
            </button>
          </div>

          {/* Optional map picker */}
          {showMap && (
            <GeoMapPicker
              radii={radii}
              polygons={polygons}
              onChange={({ radii: r, polygons: p }) => {
                setRadii(r);
                setPolygons(p);
              }}
            />
          )}

          {/* Mode toggle */}
          <div>
            <div className="label-mono mb-2">mode</div>
            <div className="flex items-center gap-1 bg-[#050505] p-1 border border-white/10">
              {["allow", "block"].map((m) => (
                <button
                  key={m}
                  data-testid={`geo-mode-${m}`}
                  onClick={() => setMode(m)}
                  className={`flex-1 font-mono text-[11px] uppercase tracking-wider py-2 transition-colors ${
                    mode === m
                      ? m === "allow"
                        ? "bg-[#ccff00] text-black font-bold"
                        : "bg-red-500 text-white font-bold"
                      : "text-zinc-500 hover:text-white"
                  }`}
                >
                  {m === "allow" ? "Allow-list (only these)" : "Block-list (everyone except these)"}
                </button>
              ))}
            </div>
          </div>

          {/* Countries */}
          <div>
            <div className="label-mono mb-2">countries (ISO-3166 alpha-2)</div>
            <div className="flex items-center gap-2 mb-2">
              <Input
                data-testid="geo-country-input"
                value={countryInput}
                onChange={(e) => setCountryInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCountry())}
                maxLength={2}
                placeholder="US"
                className="bg-[#121212] border-white/10 h-9 rounded-none font-mono text-xs w-24"
              />
              <button
                data-testid="geo-country-add"
                onClick={addCountry}
                className="border border-white/10 hover:border-[#ccff00] hover:text-[#ccff00] text-zinc-400 py-1.5 px-3 text-[10px] font-mono uppercase tracking-wider"
              >
                add
              </button>
              <div className="flex-1 flex flex-wrap gap-1 text-[9px]">
                {POPULAR_COUNTRIES.filter((c) => !countries.includes(c)).slice(0, 10).map((c) => (
                  <button
                    key={c}
                    onClick={() => setCountries([...countries, c])}
                    className="font-mono uppercase text-zinc-600 hover:text-[#ccff00] hover:underline"
                  >
                    +{c}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="geo-countries-chips">
              {countries.map((c) => (
                <Chip
                  key={c}
                  label={c}
                  onRemove={() => setCountries(countries.filter((x) => x !== c))}
                  testid={`geo-chip-country-${c}`}
                />
              ))}
              {countries.length === 0 && <span className="text-[10px] text-zinc-600 font-mono">none — matches any country</span>}
            </div>
          </div>

          {/* States */}
          <div>
            <div className="label-mono mb-2">states / regions (COUNTRY-CODE)</div>
            <div className="flex items-center gap-2 mb-2">
              <Input
                data-testid="geo-state-input"
                value={stateInput}
                onChange={(e) => setStateInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addState())}
                placeholder="US-CA"
                className="bg-[#121212] border-white/10 h-9 rounded-none font-mono text-xs w-32"
              />
              <button
                data-testid="geo-state-add"
                onClick={addState}
                className="border border-white/10 hover:border-[#ccff00] hover:text-[#ccff00] text-zinc-400 py-1.5 px-3 text-[10px] font-mono uppercase tracking-wider"
              >
                add
              </button>
              <div className="flex-1 flex flex-wrap gap-1 text-[9px]">
                {US_STATES.filter((s) => !states.includes(s)).slice(0, 8).map((s) => (
                  <button
                    key={s}
                    onClick={() => setStates([...states, s])}
                    className="font-mono uppercase text-zinc-600 hover:text-[#ccff00] hover:underline"
                  >
                    +{s}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {states.map((s) => (
                <Chip
                  key={s}
                  label={s}
                  onRemove={() => setStates(states.filter((x) => x !== s))}
                  testid={`geo-chip-state-${s}`}
                />
              ))}
            </div>
          </div>

          {/* Cities */}
          <div>
            <div className="label-mono mb-2">cities</div>
            <div className="flex items-center gap-2 mb-2">
              <Input
                data-testid="geo-city-input"
                value={cityInput}
                onChange={(e) => setCityInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCity())}
                placeholder="San Francisco"
                className="bg-[#121212] border-white/10 h-9 rounded-none font-mono text-xs flex-1"
              />
              <button
                data-testid="geo-city-add"
                onClick={addCity}
                className="border border-white/10 hover:border-[#ccff00] hover:text-[#ccff00] text-zinc-400 py-1.5 px-3 text-[10px] font-mono uppercase tracking-wider"
              >
                add
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {cities.map((c) => (
                <Chip
                  key={c}
                  label={c}
                  onRemove={() => setCities(cities.filter((x) => x !== c))}
                  testid={`geo-chip-city-${c}`}
                />
              ))}
            </div>
          </div>

          {/* Radius */}
          <div>
            <div className="label-mono mb-2">radius pins (lat / lon / km)</div>
            <div className="grid grid-cols-12 gap-1.5 mb-2">
              <Input
                data-testid="geo-radius-lat"
                value={radiusForm.lat}
                onChange={(e) => setRadiusForm({ ...radiusForm, lat: e.target.value })}
                placeholder="lat"
                className="col-span-2 bg-[#121212] border-white/10 h-9 rounded-none font-mono text-xs"
              />
              <Input
                data-testid="geo-radius-lon"
                value={radiusForm.lon}
                onChange={(e) => setRadiusForm({ ...radiusForm, lon: e.target.value })}
                placeholder="lon"
                className="col-span-2 bg-[#121212] border-white/10 h-9 rounded-none font-mono text-xs"
              />
              <Input
                data-testid="geo-radius-km"
                type="number"
                value={radiusForm.km}
                onChange={(e) => setRadiusForm({ ...radiusForm, km: e.target.value })}
                placeholder="km"
                className="col-span-2 bg-[#121212] border-white/10 h-9 rounded-none font-mono text-xs"
              />
              <Input
                data-testid="geo-radius-label"
                value={radiusForm.label}
                onChange={(e) => setRadiusForm({ ...radiusForm, label: e.target.value })}
                placeholder="label (optional)"
                className="col-span-4 bg-[#121212] border-white/10 h-9 rounded-none font-mono text-xs"
              />
              <button
                data-testid="geo-radius-add"
                onClick={addRadius}
                className="col-span-2 border border-white/10 hover:border-[#ccff00] hover:text-[#ccff00] text-zinc-400 text-[10px] font-mono uppercase tracking-wider"
              >
                <MapPin size={11} className="inline mr-1" /> add
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {radii.map((r, i) => (
                <Chip
                  key={`${r.lat}-${r.lon}-${i}`}
                  label={`${r.label || `${r.lat.toFixed?.(2) ?? r.lat},${r.lon.toFixed?.(2) ?? r.lon}`} · ${r.km}km`}
                  onRemove={() => setRadii(radii.filter((_, j) => j !== i))}
                  testid={`geo-chip-radius-${i}`}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 pt-3 border-t border-white/5">
          <button
            data-testid="geo-save-btn"
            onClick={save}
            disabled={saving}
            className="bg-[#ccff00] text-black hover:bg-[#b3e600] disabled:opacity-40 font-semibold py-2.5 px-4 flex items-center gap-2 transition-colors"
          >
            <FloppyDisk size={13} weight="fill" />
            {saving ? "Saving…" : "Save geo rule"}
          </button>
          <button
            data-testid="geo-reset-btn"
            onClick={clearAll}
            disabled={saving}
            className="border border-white/10 hover:border-red-500 hover:text-red-400 text-zinc-400 py-2.5 px-4 flex items-center gap-2 font-mono text-xs uppercase tracking-wider transition-colors"
          >
            <Trash size={11} />
            clear all
          </button>
          <div className="flex-1" />
          <button
            data-testid="geo-cancel-btn"
            onClick={() => onOpenChange(false)}
            className="text-zinc-500 hover:text-white py-2.5 px-3 text-xs font-mono uppercase tracking-wider"
          >
            <ArrowClockwise size={11} className="inline mr-1 opacity-0" />
            cancel
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
